// 必须在所有其他导入之前导入，确保 polyfill 在其他库使用 Array.at 之前就可用
import './utils/array-at-polyfill.js'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { router } from '@/router'
import { bridge } from '@/common/bridge/methods'
import { BRIDGE_METHODS } from '@/common/bridge/constants'
import { useUserStore } from '@/pinia/user'
import { handleBridgeResponse } from '@/common/functions/handleBridgeResponse'
import i18n from './i18n'
import 'vant/lib/index.css'
import './index.css'
import './assets/styles/tailwind.css'
import './theme/index.scss' // 预先加载全局初始化css
import './assets/font/iconfont.css'
import './assets/styles/fonts.scss'
import App from './App.vue'
import VConsolePlugin from './utils/vconsole'
import qs from 'qs'
// 导入 canvas polyfill，解决 pdfh5 兼容性问题
import './utils/canvas-polyfill.js'
// 导入 Array.at polyfill，解决 marked.js 兼容性问题
import './utils/array-polyfill.js'
import { isInWeChatMiniProgram } from '@/utils/tool.js'
import { getCouponWindowDetail } from '@/service/api/coupon'
// 导入埋点指令
import trackDirective from './directives/track'
import { VueMasonryPlugin } from 'vue-masonry'
import axios from 'axios'
import { init, captureException } from '@umengfe/apm'
import { syncLoginToken } from '@/hongkongimprint/composables/useAppState'

// 创建应用实例
const app = createApp(App)

app.directive('longpress', {
  mounted(el, binding) {
    let pressTimer = null
    let startX = 0
    let startY = 0
    const moveThreshold = 10 // 滑动超过 10px 视为滚动，不触发长按

    const start = e => {
      el.style.webkitTouchCallout = 'none'
      el.style.webkitUserSelect = 'none'
      el.style.khtmlUserSelect = 'none'
      el.style.mozUserSelect = 'none'
      el.style.msUserSelect = 'none'
      el.style.userSelect = 'none'

      const point = e.touches ? e.touches[0] : e
      startX = point.clientX
      startY = point.clientY

      if (pressTimer === null) {
        pressTimer = setTimeout(() => {
          // 长按1000毫秒后执行回调，避免误触举报
          handler(e)
        }, 1000)
      }
    }

    const cancel = () => {
      if (pressTimer !== null) {
        clearTimeout(pressTimer)
        pressTimer = null
      }
    }

    const onMove = e => {
      if (!pressTimer) return
      const point = e.touches ? e.touches[0] : e
      const deltaX = Math.abs(point.clientX - startX)
      const deltaY = Math.abs(point.clientY - startY)
      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        // 认为是滑动，取消长按
        cancel()
      }
    }

    const handler = e => {
      if (binding.value instanceof Function) {
        binding.value(e)
      }
    }

    el.addEventListener('mousedown', start, false)
    el.addEventListener('touchstart', start, false)
    el.addEventListener('mousemove', onMove, false)
    el.addEventListener('touchmove', onMove, false)
    el.addEventListener('mouseup', cancel, false)
    el.addEventListener('mouseout', cancel, false)
    el.addEventListener('touchend', cancel, false)
    el.addEventListener('touchcancel', cancel, false)
  },
  unmounted(el) {
    // 这里暂时不移除事件监听，保持与原逻辑一致
  },
})

// 安装 pinia、router 和 i18n
app.use(createPinia()).use(router).use(i18n)

app.use(VConsolePlugin)

app.use(VueMasonryPlugin)
// 注册埋点指令
app.directive('track', trackDirective)
// 开发环境注入调试用户信息（通过 .env.development 配置，不影响生产）
console.log('import.meta.env.MODE',import.meta.env.MODE);

if (import.meta.env.MODE === 'development') {
  const debugUserInfo = {
    mobile: import.meta.env.VITE_DEBUG_MOBILE,
    loginToken: import.meta.env.VITE_DEBUG_LOGIN_TOKEN,
    loginId: import.meta.env.VITE_DEBUG_LOGIN_ID,
    memberId: import.meta.env.VITE_DEBUG_MEMBER_ID,
  }
  Object.entries(debugUserInfo).forEach(([key, value]) => {
    if (value) sessionStorage.setItem(key, value)
  })
}

const userStore = useUserStore()
// 调试的时候塞入memberId和terminalCode
// sessionStorage.setItem('memberId', '1c1c7e09966a4b71a26cfd1a91e91685')
sessionStorage.setItem('terminalCode', '30')
sessionStorage.setItem('channelId', '153')
sessionStorage.setItem('sourceFlag', '14|75|76')
// sessionStorage.setItem('loginToken', '6a3a8b4dcb754d7db858792d21eca3ca1779946268604')

// userStore.setUserInfo({
//   mobile: '15072313771',
//   memberId: '13ee3545f2494ad6b70c330deaf2a39e',
//   loginId: 'U2001632542',
//   loginToken: '5482ab984cf34a9f85f56b2093315d6c1766125093130',
// })
// 微信小程序环境参数获取函数
const getWeChatMiniProgramParams = async () => {
  const search = window.location.search.replace('?', '')
  const urlParams = qs.parse(search) || {}
  const {
    mobile = '',
    memberId = '',
    loginToken = '',
    loginId = '',
    code = '',
    sourceFlag = '',
    showLoginSuccessModal = '',
    client: miniClient = 'wxapp',
    version: miniVersion = 'v6.0.13',
    cuid: miniCuid = '39ca90fa-eed9-4505-a877-65ed397d8199',
  } = urlParams

  if (isInWeChatMiniProgram()) {
    const versionStr = miniVersion
    const requestFrom = `client=${miniClient};domain=common;version=${versionStr};cuid=${miniCuid}`
    axios.defaults.headers.common['Request-From'] = requestFrom
    sessionStorage.setItem('Request-From', requestFrom)

    // 如果sourceFlag存在，则设置到sessionStorage中
    sourceFlag && sessionStorage.setItem('sourceFlag', sourceFlag)
    sessionStorage.setItem('terminalCode', '04')

    // 将所有URL参数存储到sessionStorage中，供埋点使用
    sessionStorage.setItem('wxappBaseParams', JSON.stringify(urlParams))
    // 清空store中的用户信息
    userStore.setUserInfo({})

    userStore.setUserInfo({
      mobile,
      memberId,
      loginId,
      loginToken,
    })
    if (showLoginSuccessModal === '1') {
      getCouponWindowDetail().then(resJson => {
        const data = resJson?.data ?? resJson ?? {}
        const { newUserFlag = false, userSource = '', picUrl = '' } = data
        sessionStorage.setItem(
          'loginSuccessModal',
          JSON.stringify({ newUserFlag, userSource, picUrl })
        )
      })
    }

    // 先清空再设置新的用户信息
    if (loginToken) {
      // 路由带了token，重新给ss ls设置一下
      sessionStorage.removeItem('loginToken')
      sessionStorage.setItem('loginToken', loginToken)
      localStorage.removeItem('loginToken')
    } else {
      // 路由没带token
      // 先从sessionStorage或localStorage中获取token
      const loginToken = sessionStorage.getItem('loginToken') || localStorage.getItem('loginToken')
      if (loginToken) {
        // 如果sessionStorage或localStorage中有token
        // 再设置新的token
        sessionStorage.removeItem('loginToken')
        sessionStorage.setItem('loginToken', loginToken)
        localStorage.removeItem('loginToken')
      }
    }
    if (memberId) {
      sessionStorage.removeItem('memberId')
      sessionStorage.setItem('memberId', memberId)
      localStorage.removeItem('memberId')
    } else {
      const memberId = sessionStorage.getItem('memberId') || localStorage.getItem('memberId')
      if (memberId) {
        sessionStorage.removeItem('memberId')
        sessionStorage.setItem('memberId', memberId)
        localStorage.removeItem('memberId')
      }
    }
    if (mobile) {
      sessionStorage.removeItem('mobile')
      sessionStorage.setItem('mobile', mobile)
      localStorage.removeItem('mobile')
    } else {
      const mobile = sessionStorage.getItem('mobile') || localStorage.getItem('mobile')
      if (mobile) {
        sessionStorage.removeItem('mobile')
        sessionStorage.setItem('mobile', mobile)
        localStorage.removeItem('mobile')
      }
    }
    if (loginId) {
      sessionStorage.removeItem('loginId')
      sessionStorage.setItem('loginId', loginId)
      localStorage.removeItem('loginId')
    } else {
      const loginId = sessionStorage.getItem('loginId') || localStorage.getItem('loginId')
      if (loginId) {
        sessionStorage.removeItem('loginId')
        sessionStorage.setItem('loginId', loginId)
        localStorage.removeItem('loginId')
      }
    }
    if (code) {
      sessionStorage.removeItem('code')
      sessionStorage.setItem('code', code)
      localStorage.removeItem('code')
    } else {
      const code = sessionStorage.getItem('code') || localStorage.getItem('code')
      if (code) {
        sessionStorage.removeItem('code')
        sessionStorage.setItem('code', code)
        localStorage.removeItem('code')
      }
    }

    return Promise.resolve('微信小程序参数获取完成')
  }
  return Promise.resolve('非微信小程序环境')
}
// 等待路由和用户信息都准备就绪后再挂载应用
const promisArr = [router.isReady]
const hasFlutter = window.flutter_inappwebview
console.log('=== 开始 Promise.all 执行 ===')
Promise.all([
  router.isReady(),
  getWeChatMiniProgramParams(),
  hasFlutter
    ? bridge
        .invokeSync(BRIDGE_METHODS.GET_USER_INFO)
        .then(res => res && handleBridgeResponse(res, userStore.setUserInfo, '获取用户信息成功'))
        .catch(error => console.error('获取用户信息失败:', error))
    : '',
  hasFlutter
    ? bridge
        .invokeSync(BRIDGE_METHODS.GET_VERSION)
        .then(res => {
          const versionInfo = res?.result?.data || {}
          const client = versionInfo.platform || 'android'
          const rawVersion = versionInfo.version || '6.0.13'
          const version = `v${rawVersion}`
          const cuid = versionInfo.deviceId || '39ca90fa-eed9-4505-a877-65ed397d8199'
          const requestFrom = `client=${client};domain=common;version=${version};cuid=${cuid}`
          sessionStorage.setItem('Request-From', requestFrom)
          axios.defaults.headers.common['Request-From'] = requestFrom
          return res
        })
        .catch(error => console.error('获取版本号失败:', error))
    : '',
  // hasFlutter ? bridge.invokeSync(BRIDGE_METHODS.GET_LOCATION)
  // .then(res => handleBridgeResponse(res, userStore.setLocationInfo, '获取定位信息成功'))
  // .catch(error => console.error('获取定位信息失败:', error)) : ''
]).then(() => {
  // 同步 hongkongimprint 模块的登录状态（异步初始化后 sessionStorage 已写入 loginToken）
  syncLoginToken()

  const umengKey = import.meta.env.VITE_UMENG_APPKEY
  if (umengKey) {
    console.log('初始化UMENG');

    init({
      pageFilter: {
        mode: 'ignore',
        rules: [],
      },
      pid: umengKey,
      dsn: '//errlog.umeng.com',
      logLevel: 0,
      tag: sessionStorage.getItem('Request-From') || 'new_journey_h5',
      puid: sessionStorage.getItem('memberId') || '游客',
    })
  }
  app.mount('#app')
})
